import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Modal } from '@/components/design-system/atoms/modal';
import { Button } from '@/components/design-system/atoms/button';
import { TextInput } from '@/components/design-system/atoms/text/text-input';
import { useWindowDimensions } from '@/hooks/use-window-dimensions';
import { composeClassName } from '@/utils/util_helpers';
import { useNewsletterSubscription } from '@/hooks/use-newsletter-subscription';
import { useLocale } from '@/hooks/use-locale';
import { PhoneInputField } from '@/components/design-system/molecules/phone-input';
import { IconSaxIcon } from '@/components/design-system/atoms/icon';
import { isValidEmail } from '@/utils/validation/email';
import { isBrowser } from '@/utils/util_helpers/ssr';
import { useVisualViewportFit } from './use-visual-viewport-fit';

interface NewsletterModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  cityId?: number;
  city?: string,
  houseId?: number;
  displayPhoneNumber?: boolean;
  phoneRequired?: boolean;
  source?: string;
}

const NewsletterModal: React.FC<NewsletterModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  cityId,
  city,
  houseId,
  displayPhoneNumber = false,
  phoneRequired = true,
  source,
}) => {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState('');
  const [phone, setPhone] = useState('');
  const { screenIsLargeOrEqualThan } = useWindowDimensions();
  const { subscribe, isLoading } = useNewsletterSubscription();
  const { currentLocale } = useLocale();
  const { t } = useTranslation();
  const viewportFit = useVisualViewportFit(isOpen);

  const handleSubmit = async () => {
    if (!firstName || !lastName || !email) {
      return;
    }

    if (!isValidEmail(email)) {
      setEmailError(t('js.features.marketplace.newsletter_modal.form.email.error') as string);
      return;
    }

    setEmailError('');
    try {
      await subscribe({
        firstName,
        lastName,
        email,
        cityId,
        houseId,
        language: currentLocale,
        phone: phone ? `+${phone}` : undefined,
        source,
      });

      if (isBrowser()) {
        sessionStorage.setItem('subscribed_email', email);
      }

      onSuccess();
    } catch (error) {
      // Error is already handled by the hook
    }
  };

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(e.target.value);
    if (emailError) {
      setEmailError('');
    }
  };

  return (
    <Modal open={isOpen} onClose={onClose}>
      <div
        className={composeClassName(
          'scrollbar-hide flex flex-col overflow-y-auto overscroll-contain rounded-lg bg-white shadow-medium',
          screenIsLargeOrEqualThan('lg') ? 'w-[500px] p-5' : 'w-auto max-w-lg p-4'
        )}
        style={{
          maxHeight: viewportFit.maxHeight,
          transform: viewportFit.offsetY ? `translateY(${viewportFit.offsetY}px)` : undefined,
        }}
      >
        <div className="mb-3 flex flex-col items-baseline text-center">
          <div>
            <IconSaxIcon name="news-letter" size={64} className="text-primary" strokeColor="none" />
          </div>
          <div className="flex flex-col gap-2 text-left font-sans">
            <h2 className="text-[16px] font-bold text-text-primary">
              {t('js.features.marketplace.newsletter_modal.title', { city: city })}
            </h2>
            <p className="text-[14px] text-text-secondary">
              {houseId
                ? t('js.features.marketplace.newsletter_modal.subtitle_house')
                : t('js.features.marketplace.newsletter_modal.subtitle')}
            </p>
          </div>
        </div>

        <div className="flex flex-col gap-4">
          <div className="flex gap-4">
            <div className="flex-1">
              <TextInput
                label={t('js.features.marketplace.newsletter_modal.form.first_name.label')}
                type="text"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                placeholder={t(
                  'js.features.marketplace.newsletter_modal.form.first_name.placeholder'
                )}
                required
              />
            </div>
            <div className="flex-1">
              <TextInput
                label={t('js.features.marketplace.newsletter_modal.form.last_name.label')}
                type="text"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                placeholder={t(
                  'js.features.marketplace.newsletter_modal.form.last_name.placeholder'
                )}
                required
              />
            </div>
          </div>
          <div>
            <TextInput
              label={t('js.features.marketplace.newsletter_modal.form.email.label')}
              type="email"
              value={email}
              onChange={handleEmailChange}
              placeholder={
                t('js.features.marketplace.newsletter_modal.form.email.placeholder') as string
              }
              errorMessage={emailError || undefined}
              invalid={!!emailError}
              required
            />
          </div>
          {displayPhoneNumber && (
            <div>
              <PhoneInputField
                value={phone}
                onChange={(value: string) => setPhone(value)}
                label={
                  t('js.features.marketplace.newsletter_modal.form.phone.label') || undefined
                }
                defaultCountry="be"
                placeholder="1234-567"
              />
            </div>
          )}
        </div>

        <div className="align mt-4 flex gap-4">
          <Button
            variant="primary"
            disabled={isLoading || !firstName || !lastName || !email || (displayPhoneNumber && phoneRequired && !phone)}
            onClick={handleSubmit}
            size="large"
          >
            {isLoading
              ? t('js.features.marketplace.newsletter_modal.buttons.subscribing')
              : t('js.features.marketplace.newsletter_modal.buttons.subscribe')}
          </Button>
          <Button variant="ghost" onClick={onClose} disabled={isLoading} size="large">
            {t('js.features.marketplace.newsletter_modal.buttons.cancel')}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default NewsletterModal;
